import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Drop-in replacement for `react-data-table-component`.
 *
 * Desktop (>768px): renders the original <DataTable /> with the exact same props,
 * so desktop behavior is unchanged.
 *
 * Mobile (<=768px): renders each row as a stacked card built from the SAME `columns`
 * config. Each column's existing `cell` / `selector` renderer is reused, so custom
 * cells and action buttons keep working. Columns with no `name` (action columns) are
 * rendered full-width in a card footer without a label. A column may opt out of the
 * card with `column.hideOnMobile = true`.
 *
 * Mobile also honours server pagination (paginationServer, paginationTotalRows,
 * onChangePage), the subHeader search box and selectableRows. A page may pass
 * `mobileSummaryFields` (column ids or names) to show a compact card with the
 * remaining fields behind a "Show all details" toggle; DataTable ignores it.
 *
 * Usage: just change the import in a page from
 *   import DataTable from 'react-data-table-component';
 * to
 *   import DataTable from '../components/ResponsiveDataTable';
 */
const ResponsiveDataTable = ({ mobileSummaryFields, mobileCardRenderer, ...props }) => {
  const isMobile = useIsMobile();

  // If forceDesktop prop is set, always render desktop table with horizontal scroll
  if (props.forceDesktop) {
    return (
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <DataTable {...props} />
      </div>
    );
  }

  if (!isMobile) {
    return <DataTable {...props} />;
  }

  return <MobileCardList {...props} mobileSummaryFields={mobileSummaryFields} mobileCardRenderer={mobileCardRenderer} />;
};

const MobileCardList = ({
  columns = [],
  data = [],
  keyField = 'id',
  progressPending = false,
  progressComponent,
  noDataComponent,
  pagination = false,
  paginationServer = false,
  paginationTotalRows,
  paginationDefaultPage = 1,
  paginationPerPage = 10,
  onChangePage,
  defaultSortFieldId,
  defaultSortAsc = true,
  subHeader = false,
  subHeaderComponent,
  selectableRows = false,
  selectableRowDisabled,
  onSelectedRowsChange,
  clearSelectedRows,
  mobileSummaryFields,
  mobileCardRenderer,
}) => {
  const [page, setPage] = useState(paginationDefaultPage || 1);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Reset to first page when the underlying data set changes size (e.g. new filter).
  // With server pagination the parent owns the page, so follow it instead.
  useEffect(() => {
    if (!paginationServer) setPage(1);
  }, [data.length, paginationServer]);

  useEffect(() => {
    if (paginationServer) setPage(paginationDefaultPage || 1);
  }, [paginationDefaultPage, paginationServer]);

  // Same contract as react-data-table-component: toggling clearSelectedRows clears.
  useEffect(() => {
    setSelectedKeys([]);
  }, [clearSelectedRows]);

  const rowKeyOf = (row, index) => (row[keyField] != null ? row[keyField] : index);

  const emitSelection = (keys) => {
    setSelectedKeys(keys);
    if (onSelectedRowsChange) {
      const selectedRows = data.filter((row, i) => keys.includes(rowKeyOf(row, i)));
      onSelectedRowsChange({
        allSelected: selectedRows.length > 0 && selectedRows.length === data.length,
        selectedCount: selectedRows.length,
        selectedRows,
      });
    }
  };

  const subHeaderNode = subHeader && subHeaderComponent ? (
    <div className="rdt-mobile-subheader">{subHeaderComponent}</div>
  ) : null;

  if (progressPending) {
    return (
      <>
        {subHeaderNode}
        <div className="rdt-mobile-state">
          {progressComponent || 'Loading...'}
        </div>
      </>
    );
  }

  if (!data || data.length === 0) {
    return (
      <>
        {subHeaderNode}
        <div className="rdt-mobile-state">
          {noDataComponent || 'There are no records to display'}
        </div>
      </>
    );
  }

  const visibleColumns = columns.filter((col) => !col.omit && !col.hideOnMobile);

  // Apply the same default sort that the desktop DataTable uses.
  let sortedData = data;
  if (defaultSortFieldId && !paginationServer) {
    const sortCol = columns.find(
      (col) => col.id === defaultSortFieldId || col.key === defaultSortFieldId
    );
    const selector = sortCol?.selector;
    if (typeof selector === 'function') {
      sortedData = [...data].sort((a, b) => {
        const valA = selector(a);
        const valB = selector(b);
        if (valA == null && valB == null) return 0;
        if (valA == null) return defaultSortAsc ? -1 : 1;
        if (valB == null) return defaultSortAsc ? 1 : -1;
        const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
        return defaultSortAsc ? cmp : -cmp;
      });
    }
  }

  const totalCount = paginationServer ? (paginationTotalRows || data.length) : sortedData.length;
  const totalPages = pagination ? Math.max(1, Math.ceil(totalCount / paginationPerPage)) : 1;
  const safePage = Math.min(page, totalPages);
  const rows = pagination && !paginationServer
    ? sortedData.slice((safePage - 1) * paginationPerPage, safePage * paginationPerPage)
    : sortedData;

  const goToPage = (next) => {
    setPage(next);
    if (onChangePage) onChangePage(next, totalCount);
  };

  const renderValue = (col, row, index) => {
    if (typeof col.cell === 'function') {
      return col.cell(row, index, col, index);
    }
    if (typeof col.format === 'function') {
      return col.format(row, index);
    }
    if (typeof col.selector === 'function') {
      return col.selector(row);
    }
    return null;
  };

  const isSummaryField = (col) =>
    mobileSummaryFields.includes(col.id) || mobileSummaryFields.includes(col.name);

  const selectableKeys = selectableRows
    ? rows
        .map((row, i) => (selectableRowDisabled && selectableRowDisabled(row) ? null : rowKeyOf(row, i)))
        .filter((k) => k != null)
    : [];
  const allOnPageSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedKeys.includes(k));

  return (
    <div className="rdt-mobile-cards">
      {subHeaderNode}

      {selectableRows && (
        <label className="rdt-mobile-select-all">
          <input
            type="checkbox"
            checked={allOnPageSelected}
            onChange={() =>
              emitSelection(
                allOnPageSelected
                  ? selectedKeys.filter((k) => !selectableKeys.includes(k))
                  : Array.from(new Set([...selectedKeys, ...selectableKeys]))
              )
            }
          />
          <span>
            {selectedKeys.length > 0 ? `${selectedKeys.length} selected` : 'Select all on this page'}
          </span>
        </label>
      )}

      {rows.map((row, index) => {
        const rowKey = rowKeyOf(row, index);
        if (typeof mobileCardRenderer === 'function') {
          return (
            <div className="rdt-mobile-card rdt-mobile-card-custom" key={rowKey}>
              {mobileCardRenderer(row, index)}
            </div>
          );
        }
        const labelled = visibleColumns.filter((col) => col.name);
        const actions = visibleColumns.filter((col) => !col.name);
        const expanded = expandedKeys.includes(rowKey);
        const summary = mobileSummaryFields ? labelled.filter(isSummaryField) : labelled;
        const extra = mobileSummaryFields ? labelled.filter((col) => !isSummaryField(col)) : [];
        const shown = expanded ? summary.concat(extra) : summary;
        const disabled = selectableRowDisabled && selectableRowDisabled(row);
        const checked = selectedKeys.includes(rowKey);

        return (
          <div className={`rdt-mobile-card${checked ? ' selected' : ''}`} key={rowKey}>
            {selectableRows && (
              <label className="rdt-mobile-select">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() =>
                    emitSelection(checked ? selectedKeys.filter((k) => k !== rowKey) : [...selectedKeys, rowKey])
                  }
                />
                <span>Select</span>
              </label>
            )}
            {shown.map((col, ci) => (
              <div className="rdt-mobile-row" key={col.id || col.name || ci}>
                <span className="rdt-mobile-label">{col.name}</span>
                <span className="rdt-mobile-value">{renderValue(col, row, index)}</span>
              </div>
            ))}
            {extra.length > 0 && (
              <button
                type="button"
                className="rdt-mobile-more"
                onClick={() =>
                  setExpandedKeys((prev) => (expanded ? prev.filter((k) => k !== rowKey) : [...prev, rowKey]))
                }
              >
                {expanded ? 'Show less ▴' : `Show all details (${extra.length} more) ▾`}
              </button>
            )}
            {actions.length > 0 && (
              <div className="rdt-mobile-actions">
                {actions.map((col, ci) => (
                  <React.Fragment key={col.id || ci}>
                    {renderValue(col, row, index)}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {pagination && totalPages > 1 && (
        <div className="rdt-mobile-pager">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={safePage <= 1}
            onClick={() => goToPage(Math.max(1, safePage - 1))}
          >
            Prev
          </button>
          <span className="rdt-mobile-pager-info">
            Page {safePage} of {totalPages}
            {paginationServer && <> · {totalCount} total</>}
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={safePage >= totalPages}
            onClick={() => goToPage(Math.min(totalPages, safePage + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ResponsiveDataTable;
